import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { DEFAULT_TOAST_CONFIG, IToast, IToastConfig, IToastEntity } from "./mainUtils";

type ToastInput = Omit<IToast, "id"> & { id?: string };

export interface ToastsState {
    config: IToastConfig;
    toasts: IToastEntity[];
}

const initialState: ToastsState = {
    config: DEFAULT_TOAST_CONFIG,
    toasts: [],
};

const toastsSlice = createSlice({
    name: "toasts",
    initialState,
    reducers: {
        setConfig(state, action: PayloadAction<Partial<IToastConfig>>) {
            state.config = { ...state.config, ...action.payload };
        },
        addToQueue(state, action: PayloadAction<ToastInput | ToastInput[]>) {
            const toAdd = Array.isArray(action.payload) ? action.payload : [action.payload];
            const now = Date.now();

            const normalized = toAdd.map<IToastEntity>((toast, index) => {
                const id = toast.id ?? createToastID();
                const durationMs = toast.durationMs ?? state.config.durationMs;

                return {
                    ...toast,
                    id,
                    createdAt: now + index,
                    durationMs,
                };
            });

            state.toasts.push(...normalized);

            const maxVisible = Math.max(1, state.config.maxVisible);
            while (state.toasts.length > maxVisible) {
                state.toasts.shift();
            }
        },
        removeFromQueue(state, action: PayloadAction<string | string[]>) {
            const ids = new Set(Array.isArray(action.payload) ? action.payload : [action.payload]);
            state.toasts = state.toasts.filter((toast) => !ids.has(toast.id));
        },
        shiftQueue(state) {
            state.toasts.shift();
        },
        reset(state) {
            state.config = DEFAULT_TOAST_CONFIG;
            state.toasts = [];
        },
    },
});

export const ToastsActions = toastsSlice.actions;

export default toastsSlice.reducer;

function createToastID(): string {
    return `toast-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}
